import { Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setFilterDataProducts,
  setFilterExtendedStatsDomains,
  setFilterIntervallo,
  fetchExtendedStatsDomains,
  fetchDataLineage,
  fetchVersionLineage,
} from "../../store/app/DataQualityStatus/DataQualitySlice";

import { Button, FormGroup, Input, Label } from "reactstrap";



// Function to map data into a tree-like structure for rendering
const mapDataToTree = (levels, primaryKey) => {
  const nodes = {};
  levels.forEach((level) => {
    level.points.forEach((point) => {
      nodes[point[primaryKey]] = {
        ...point,
        level: level.level,
        children: [],
      };
    });
  });

  // Assign parent-child relationships
  Object.values(nodes).forEach((node) => {
    node.parentIds.forEach((parentId) => {
      if (nodes[parentId]) {
        nodes[parentId].children.push(node);
      }
    });
  });

  // Find root nodes (those with no parent)
  const roots = Object.values(nodes).filter(
    (node) => node.parentIds.length === 0
  );
  return roots.length > 0 ? roots : Object.values(nodes);
};

const generateNode = (item, suffix, primaryKey) => {
  const node = {};
  const originID = item["origin_" + primaryKey];

  // Format reference_date to YYYY-MM-DD
  const formatDate = (date) => new Date(date).toISOString().split("T")[0];

  // Add all non-origin values to the node
  Object.keys(item).forEach((key) => {
    if (!key.startsWith("origin_") && !key.endsWith("Origin")) {
      node[key] = item[key];
    }
  });

  // Update non-origin values with origin values if suffix is "origin_" (origin node)
  if (suffix === "origin_") {
    Object.keys(item).forEach((key) => {
      if (key.startsWith("origin_") || key.endsWith("Origin")) {
        const nonOriginKey = key.replace("origin_", "").replace("Origin", "");
        if (node.hasOwnProperty(nonOriginKey)) {
          node[nonOriginKey] = item[key];
        }
      }
    });

    // For origin nodes, use origin_position for vertical positioning
    if (item.hasOwnProperty("origin_position")) {
      node.position = item.origin_position;
    }
  } else {
    // For regular nodes, use position property directly
    if (item.hasOwnProperty("position")) {
      node.position = item.position;
    }
  }

  // Set label according to primary key type
  if (primaryKey == "version_id") {
    node["reference_date"] = formatDate(node.reference_date);
    node["label"] = `${node[primaryKey]} - ${node["product_name"]}`;
  } else if (primaryKey == "type_id") {
    node["label"] = node.type_name;
  }

  // Set parent IDs
  if (originID && suffix != "origin_") {
    node["parentIds"] = [originID.toString()];
  } else {
    node["parentIds"] = [];
  }

  return node;
};

const normalizeData = (payload, params) => {
  const levelsMap = new Map();
  const nodesMap = new Map();
  // Track self-loops separately
  const selfLoops = [];

  // Find the minimum origin_generation to normalize levels
  const minOriginGeneration = Math.min(
    ...payload.map((item) => item.origin_generation)
  );

  payload.forEach((item) => {
    // Normalize levels so that the minimum origin_generation becomes level 0
    const level = item.generation - minOriginGeneration;
    const originLevel = item.origin_generation - minOriginGeneration;
    const keyFrom = "origin_" + params.primaryKey;
    const keyTo = params.primaryKey;

    // Check for self-loop: same entity ID but different generation
    if (
      item[keyFrom] === item[keyTo] &&
      item.origin_generation !== item.generation
    ) {
      selfLoops.push({
        entityId: item[keyTo],
        fromGeneration: item.origin_generation,
        toGeneration: item.generation,
        item: item,
      });
    } else if (
      item[keyFrom] === item[keyTo] &&
      item.origin_generation === item.generation
    ) {
      // Case: same entity, same generation (self-reference)
      // Treat as self-loop with minimal offset
      selfLoops.push({
        entityId: item[keyTo],
        fromGeneration: item.origin_generation,
        toGeneration: item.generation,
        item: item,
        isSameGeneration: true, // Flag to handle differently
      });
    }

    if (!nodesMap.has(item[keyFrom])) {
      const originNode = generateNode(item, "origin_", keyTo);

      if (!levelsMap.has(originLevel)) {
        levelsMap.set(originLevel, []);
      }
      levelsMap.get(originLevel).push(originNode);
      nodesMap.set(item[keyFrom], originNode);
    }

    // Add destination node (child)
    if (!nodesMap.has(item[keyTo])) {
      const destinationNode = generateNode(item, "", keyTo);

      if (!levelsMap.has(level)) {
        levelsMap.set(level, []);
      }
      levelsMap.get(level).push(destinationNode);
      nodesMap.set(item[keyTo], destinationNode);
    } else {
      // If the node already exists, update its parentIds to include the new parent
      const existingNode = nodesMap.get(item[keyTo]);

      if (
        item[keyFrom] &&
        !existingNode.parentIds.includes(item[keyFrom].toString())
      )
        existingNode.parentIds.push(item[keyFrom].toString());
    }
  });

  const levels = Array.from(levelsMap.entries())
    .map(([level, points]) => ({
      level,
      count: points.length,
      points,
    }))
    .sort((a, b) => a.level - b.level);

  const totalLevels = levels.length;
  const totalPoints = nodesMap.size;

  return {
    summary: {
      totalLevels,
      totalPoints,
    },
    levels,
    selfLoops, // Include the self-loops in the return value
  };
};

// Helper function to calculate node position (X, Y coordinates)
const calculateNodePosition = (
  node,
  levelWidth,
  levelSpacing,
  nodeSpacing,
  maxPointsPerLevel
) => {
  // Horizontal positioning remains the same (based on level/generation)
  const x = node.level * levelSpacing + levelWidth * node.level + 100;

  // Use position property instead of index for vertical positioning
  // Default to node.index if position is not available
  const verticalPosition = parseInt(node.position) || node.index || 0;

  const maxLevelHeight = maxPointsPerLevel * nodeSpacing;
  const yOffset = (maxLevelHeight - node.levelCount * nodeSpacing) / 2 + 100;
  const y = verticalPosition * nodeSpacing + yOffset;

  return { x, y };
};

const LineageDrawer = ({ data, params = {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [optimizeLayout, setOptimizeLayout] = useState(false);
  const [validOnly, setValidOnly] = useState(!!params.initialValidOnly);

  const svgRef = useRef(null);
  const containerRef = useRef(null);


  const normalizedData = normalizeData(data, params);



  const levelWidth = 100;
  const levelSpacing = 150;
  const nodeSpacing = 100;
  const maxPointsPerLevel = Math.max(
    ...normalizedData.levels.map((level) => level.count)
  );


  const maxPosition = Math.max(
    ...normalizedData.levels.flatMap(level =>
      level.points.map(point => parseInt(point.position) || 0)
    ),
    maxPointsPerLevel
  );

  const svgWidth =
    normalizedData.summary.totalLevels * (levelWidth + levelSpacing);
  const svgHeight = Math.max(maxPosition * nodeSpacing + 200, maxPointsPerLevel * nodeSpacing + 200);

  const searchParams = new URLSearchParams(location.search);
  const getCleanSearchParams = () => {
    const cleanSearchParams = new URLSearchParams(location.search);
    cleanSearchParams.delete("validOnly");
    return cleanSearchParams;
  };

  const extendedStatsDomains = useSelector(
    (state) => state.dataQualityReducer.extendedStatsDomains
  );

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState({});

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipNode, setTooltipNode] = useState("");

  const [lineTooltipVisible, setLineTooltipVisible] = useState(false);
  const [lineTooltipPosition, setLineTooltipPosition] = useState({
    x: 0,
    y: 0,
  });
  const [lineTooltip, setLineTooltip] = useState("");

  const [highlightedLine, setHighlightedLine] = useState("");
  const [highlightedNode, setHighlightedNode] = useState("");

  const [zoomLevel, setZoomLevel] = useState(1);
  const [transformOrigin, setTransformOrigin] = useState({ x: 0, y: 0 });

  const [legendVisible, setLegendVisible] = useState(false);

  const [legendPosition, setLegendPosition] = useState(() => {
    const legendWidth = 400;
    const x =
      typeof window !== "undefined"
        ? Math.max(window.innerWidth - legendWidth, 10)
        : 100;
    return { x, y: 100 };
  });

  const [draggingLegend, setDraggingLegend] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleLegendMouseDown = (e) => {
    setDraggingLegend(true);
    setDragOffset({
      x: e.clientX - legendPosition.x,
      y: e.clientY - legendPosition.y,
    });
    e.stopPropagation();
    e.preventDefault();
  };

  const handleContainerMouseDown = (e) => {
    if (legendVisible) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    e.preventDefault();
  };
  const handleContainerMouseMove = (e) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
  };
  const handleContainerMouseUp = () => setIsPanning(false);

  const fitSvg = () => {
    // Get the bounding box of all SVG content
    const bbox = svgRef.current.getBBox();
    // Use the larger of calculated or actual content size
    const contentWidth = Math.max(svgWidth, bbox.width + bbox.x);
    const contentHeight = Math.max(svgHeight, bbox.height + bbox.y);

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight - 100; // leave space for controls

    const scaleX = windowWidth / contentWidth;
    const scaleY = windowHeight / contentHeight;
    const fitScale = Math.min(scaleX, scaleY, 1); // never zoom in by default

    // Center the SVG content
    const offsetX = (windowWidth - bbox.width * fitScale) / 2 - bbox.x * fitScale - 150; // Add extra 50px to center more
    const offsetY = (windowHeight - bbox.height * fitScale) / 2 - bbox.y * fitScale;


    setZoomLevel(fitScale);
    setTransformOrigin({ x: 0, y: 0 });
    setPan({ x: offsetX, y: offsetY });
  };

  const handleZoomIn = (event) => {
    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportCenterX = containerRect.width / 2;
    const viewportCenterY = containerRect.height / 2;

    // Calculate how much the pan needs to be adjusted to keep center stable
    const scaleFactor = 1.2;
    const newZoomLevel = Math.min(zoomLevel * scaleFactor, 5);

    // Formula: new pan = viewportCenter - (viewportCenter - oldPan) * newZoom/oldZoom
    setPan({
      x: viewportCenterX - (viewportCenterX - pan.x) * (newZoomLevel / zoomLevel),
      y: viewportCenterY - (viewportCenterY - pan.y) * (newZoomLevel / zoomLevel)
    });

    // Then set the new zoom level
    setZoomLevel(newZoomLevel);
    // Keep transform origin at top-left for consistency
    setTransformOrigin({ x: 0, y: 0 });
  };

  const handleZoomOut = (event) => {
    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportCenterX = containerRect.width / 2;
    const viewportCenterY = containerRect.height / 2;

    // Calculate how much the pan needs to be adjusted
    const scaleFactor = 1 / 1.2;
    const newZoomLevel = Math.max(zoomLevel * scaleFactor, 0.2);

    // Same formula for zooming out
    setPan({
      x: viewportCenterX - (viewportCenterX - pan.x) * (newZoomLevel / zoomLevel),
      y: viewportCenterY - (viewportCenterY - pan.y) * (newZoomLevel / zoomLevel)
    });

    setZoomLevel(newZoomLevel);
    setTransformOrigin({ x: 0, y: 0 });
  };

  const handleResetZoom = () => {
    fitSvg();
  };



  const onToggleOptimized = (e) => {
    const newVal = !!e.target.checked;
    setOptimizeLayout(newVal);

    // Use the new value directly from the event with correct parameter name
    if (params.primaryKey === "type_id") {
      // data lineage
      dispatch(fetchDataLineage(params.originId, { optimizeLayout: newVal }));
    } else if (params.primaryKey === "version_id") {
      // version lineage
      dispatch(fetchVersionLineage(params.originId, { optimizeLayout: newVal, validOnly }));
    }
  };

  const onToggleValidOnly = (e) => {
    const newVal = !!e.target.checked;
    setValidOnly(newVal);

    // Use the new value directly from the event with correct parameter name
    if (params.primaryKey === "version_id") {
      // version lineage
      dispatch(fetchVersionLineage(params.originId, { optimizeLayout, validOnly: newVal }));
    }
  };

  useEffect(() => {
    setValidOnly(!!params.initialValidOnly);
  }, [params.initialValidOnly]);

  const handleDownloadPNG = () => {
    if (!svgRef.current) return;

    // Clone the SVG element
    const svgElement = svgRef.current.cloneNode(true);

    // Get the bounding box of all content
    const bbox = svgRef.current.getBBox();

    // Add padding
    const padding = 50;
    const width = bbox.width + bbox.x + padding * 2;
    const height = bbox.height + bbox.y + padding * 2;

    // Set proper viewBox and dimensions
    svgElement.setAttribute('viewBox', `${bbox.x - padding} ${bbox.y - padding} ${width} ${height}`);
    svgElement.setAttribute('width', width);
    svgElement.setAttribute('height', height);

    // Remove transform
    svgElement.style.transform = 'none';

    // Serialize SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);

    // Create canvas
    const canvas = document.createElement('canvas');
    const scale = 2; // For better quality
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    // Create image from SVG
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Draw SVG
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to PNG and download
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `lineage-${params.originId || 'diagram'}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Clean up
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(pngUrl);
      });
    };

    img.src = url;
  };

  // Add event listeners for keyboard and mouse events
  useEffect(() => {
    if (!extendedStatsDomains || extendedStatsDomains.length === 0) {
      dispatch(fetchExtendedStatsDomains());
    }

    const handleKeyDown = (event) => {
      if (event.altKey && event.key === "+") {
        event.preventDefault();
        handleZoomIn(event);
      } else if (event.altKey && event.key === "-") {
        event.preventDefault();
        handleZoomOut(event);
      }
    };

    const handleWheel = (event) => {
      if (event.altKey) {
        event.preventDefault();

        // Get the center of the viewport with pan adjustment
        const containerRect = containerRef.current.getBoundingClientRect();
        const centerX = containerRect.width / 2 - pan.x;
        const centerY = containerRect.height / 2 - pan.y;

        setTransformOrigin({ x: centerX, y: centerY });

        if (event.deltaY < 0) {
          setZoomLevel((prevZoomLevel) => Math.min(prevZoomLevel * 1.2, 5));
        } else {
          setZoomLevel((prevZoomLevel) => Math.max(prevZoomLevel / 1.2, 0.2));
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    // Use passive: false to allow preventDefault
    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel, { passive: false });
    };
  }, [pan]);

  // Ensure legend stays visible on window resize
  useEffect(() => {
    const legendWidth = 400;
    const handleResize = () => {
      setLegendPosition((prev) => {
        let newX = prev.x;
        // If legend would be outside the window, reposition it
        if (window.innerWidth - legendWidth < 10) {
          newX = 10;
        } else if (prev.x + legendWidth > window.innerWidth) {
          newX = window.innerWidth - legendWidth;
        }
        return { ...prev, x: newX };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!draggingLegend) return;
    const handleMouseMove = (e) => {
      setLegendPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };
    const handleMouseUp = () => {
      setDraggingLegend(false);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingLegend, dragOffset]);

  useEffect(() => {
    if (!svgRef.current) return;
    fitSvg();
    window.addEventListener("resize", fitSvg);
    return () => window.removeEventListener("resize", fitSvg);
  }, [svgWidth, svgHeight, data]);

  useEffect(() => {
    if (!isPanning) return;
    window.addEventListener("mousemove", handleContainerMouseMove);
    window.addEventListener("mouseup", handleContainerMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleContainerMouseMove);
      window.removeEventListener("mouseup", handleContainerMouseUp);
    };
  }, [isPanning, panStart]);


  const handleMouseDown = (e) => {
    if (legendVisible) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    e.preventDefault();
  };
  const handleMouseMove = (e) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
  };
  const handleMouseUp = () => setIsPanning(false);

  useEffect(() => {
    if (!isPanning) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isPanning, panStart]);


  // Add a check to handle empty or non-existent data
  if (!data || data.length === 0) {
    const isVersionLineage = params.primaryKey === "version_id";
    let noDataMessage;

    if (isVersionLineage) {
      noDataMessage = validOnly
        ? `Version ${params.originId} has no valid ancestors and no valid descendants`
        : `Version ${params.originId} has no ancestors and no descendants`;
    } else {
      noDataMessage = `${params.originTypeName || 'Type'} has no ancestors and no descendants`;
    }
    return (
      <div className="lineageNoData">
        <h1>{noDataMessage}</h1>
      </div>
    );
  }

  const treeData = mapDataToTree(normalizedData.levels, params.primaryKey);

  const defaultButtons = [
    {
      label: "Single Version DQ",
      action: () => {
        const cleanSearchParams = getCleanSearchParams();
        navigate(
          "/versioni/detail/" + selectedNode.version_id + "?" + cleanSearchParams
        );
      },
    },
    {
      label: "Log",
      action: () => {
        const cleanSearchParams = getCleanSearchParams();
        navigate(
          "/process-runs/detail/application-log/" +
          selectedNode.version_id +
          "?" +
          cleanSearchParams
        );
      },
    },
    {
      label: "Quick Search",
      action: () => {
        const cleanSearchParams = getCleanSearchParams();
        const matchingIds = extendedStatsDomains
          .filter((item) => item.status_id === 1 || item.status_id === -1)
          .map((item) => item.id);
        console.log("Quick Search");
        console.log("Selected Node:", selectedNode.product_id);

        dispatch(setFilterExtendedStatsDomains(matchingIds || []));
        cleanSearchParams.set("extendedStatsDomains", JSON.stringify(matchingIds));

        dispatch(setFilterDataProducts([selectedNode.product_id] || []));
        cleanSearchParams.set(
          "dataproducts",
          JSON.stringify([selectedNode.product_id])
        );

        setFilterIntervallo(-3);
        cleanSearchParams.set("interval", -3);

        navigate("/versioni?" + cleanSearchParams);
      },
    },
    {
      label: "Version Lineage",
      action: () => {
        navigate(
          "/versioni/version-lineage/" +
          selectedNode.version_id +
          "?" +
          searchParams
        );
      }
    },
    {
      label: "Data Lineage",
      action: () => {
        const cleanSearchParams = getCleanSearchParams();
        navigate(
          "/versioni/data-lineage/" + selectedNode.type_id + "?" + cleanSearchParams
        );
      }
    },
  ];







  const nodePositions = {};

  // Calculate positions for all nodes first
  const calculatePositions = (
    nodes,
    levelWidth,
    levelSpacing,
    nodeSpacing,
    levelIndices = {},
    visitedNodes = new Set()
  ) => {
    nodes.forEach((node) => {
      const nodeKey = node[params.primaryKey];
      if (visitedNodes.has(nodeKey)) return; // Skip already visited nodes
      visitedNodes.add(nodeKey);

      if (!levelIndices[node.level]) {
        levelIndices[node.level] = 0;
      }

      node.index = levelIndices[node.level]++;

      // Find the corresponding level data
      const levelData = normalizedData.levels.find(
        (l) => l.level === node.level
      );
      node.levelCount = levelData ? levelData.count : 1;

      const position = calculateNodePosition(
        node,
        levelWidth,
        levelSpacing,
        nodeSpacing,
        maxPointsPerLevel
      );

      // Validate position before storing
      if (!isNaN(position.x) && !isNaN(position.y)) {
        nodePositions[nodeKey] = position;
      } else {
        console.warn(
          `Invalid position calculated for node ${nodeKey}:`,
          position
        );
        // Provide a fallback position
        nodePositions[nodeKey] = { x: 100, y: 100 };
      }

      // Recursively calculate positions for children
      if (node.children && node.children.length > 0) {
        calculatePositions(
          node.children,
          levelWidth,
          levelSpacing,
          nodeSpacing,
          levelIndices,
          visitedNodes
        );
      }
    });
  };

  calculatePositions(treeData, levelWidth, levelSpacing, nodeSpacing);

  const openMenu = (node, event) => {
    setMenuVisible(false); // Hide the menu first to reset the animation

    setTimeout(() => {
      setTooltipVisible(false); // Hide the tooltip when the menu is opened
      unhighlightNode(); // Unhighlight the node when the menu is opened
      setMenuVisible(true);
      setMenuPosition({
        x: event.clientX + window.scrollX + 10,
        y: event.clientY + window.scrollY + 10,
      }); // Adjust position to be under the node
      setSelectedNode(node);
    }, 100); // Small delay to allow the animation to reset
  };

  // Handle node click event
  const handleNodeClick = (event, node) => {
    event.stopPropagation(); // Prevent event from bubbling up
    openMenu(node, event);
  };

  const closeMenu = () => {
    setMenuVisible(false);
    setSelectedNode({});
  };

  const showTooltip = (node, event) => {
    if (
      menuVisible &&
      selectedNode[params.primaryKey] == node[params.primaryKey]
    )
      return; // Do not show tooltip if menu is visible

    setTooltipVisible(true);
    setTooltipPosition({
      x: event.clientX + window.scrollX + 10,
      y: event.clientY + window.scrollY + 10,
    });
    setTooltipNode(node);
    highlightNode(node[params.primaryKey]);
  };

  const hideTooltip = () => {
    setTooltipVisible(false);
    unhighlightNode();
  };

  const highlightLine = (event, lineId, lineData) => {
    setHighlightedLine(lineId);
    setLineTooltipVisible(true);
    setLineTooltipPosition({
      x: event.clientX + window.scrollX,
      y: event.clientY + window.scrollY,
    });
    setLineTooltip(lineData || {});
  };

  const unhighlightLine = () => {
    setHighlightedLine("");
    setLineTooltipVisible(false);
  };

  // Resolve edge payload from raw data (not from child node)
  const getEdgeTooltipData = (parentNode, childNode) => {
    const pk = params.primaryKey;
    const originPk = `origin_${pk}`;

    const parentId = parentNode?.[pk]?.toString();
    const childId = childNode?.[pk]?.toString();

    // Best match: ids + generation pair
    let match = data.find(
      (item) =>
        item?.[originPk]?.toString() === parentId &&
        item?.[pk]?.toString() === childId &&
        item?.origin_generation === parentNode?.generation &&
        item?.generation === childNode?.generation
    );

    // Fallback: ids only
    if (!match) {
      match = data.find(
        (item) =>
          item?.[originPk]?.toString() === parentId &&
          item?.[pk]?.toString() === childId
      );
    }

    return match || {};
  };

  const highlightNode = (nodeId) => {
    setHighlightedNode(nodeId);
  };

  const unhighlightNode = () => {
    setHighlightedNode("");
  };

  // Modified renderLine function with fixed curved self-loop rendering
  const renderLine = (node, child, childIndex, isForcedSelfLoop = false) => {
    const nodePosition = nodePositions[node[params.primaryKey]];
    const childPosition = nodePositions[child[params.primaryKey]];
    const edgeTooltipData = getEdgeTooltipData(node, child);

    // Add validation to ensure both positions exist and are valid
    if (
      !nodePosition ||
      !childPosition ||
      isNaN(nodePosition.x) ||
      isNaN(nodePosition.y) ||
      isNaN(childPosition.x) ||
      isNaN(childPosition.y)
    ) {
      console.warn(
        `Invalid positions for line from ${node[params.primaryKey]} to ${child[params.primaryKey]
        }`
      );
      return null;
    }

    // Check for self-loop - either explicitly forced or when it's the same entity with different generations
    const isSelfLoop =
      isForcedSelfLoop ||
      (node[params.primaryKey] === child[params.primaryKey] &&
        node.generation !== child.generation) ||
      (node[params.primaryKey] === child[params.primaryKey] &&
        node.generation === child.generation);

    // Use a unique key based on parent-child relationship
    const lineId = `line-${node[params.primaryKey]}-${child[params.primaryKey]
      }-${childIndex}`;

    // Draw a curved self-loop if this is a self-loop
    if (isSelfLoop) {
      // "Lasso" effect: a big loop above the node
      const { x, y } = nodePosition;
      const r = 30; // loop size, adjust as needed

      // Cubic Bezier: up, right, down, back to node
      const startX = x;
      const startY = y;
      const c1x = x + r * 2;
      const c1y = y - r * 2.5;
      const c2x = x - r * 2;
      const c2y = y - r * 2.5;
      const endX = x;
      const endY = y;

      return (
        <Fragment key={lineId}>
          {/* Invisible hit area */}
          <path
            d={`M ${startX} ${startY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${endX} ${endY}`}
            stroke="transparent"
            strokeWidth="25"
            fill="none"
            onMouseEnter={(event) => highlightLine(event, lineId, edgeTooltipData)}
            onMouseLeave={unhighlightLine}
          />
          {/* Actual lasso curve */}
          <path
            d={`M ${startX} ${startY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${endX} ${endY}`}
            stroke={highlightedLine === lineId ? "blue" : "lightgrey"}
            strokeWidth={highlightedLine === lineId ? "4" : "3"}
            fill="none"
            markerEnd={
              highlightedLine === lineId ? "url(#arrow-blue)" : "url(#arrow)"
            }
          />
        </Fragment>
      );
    }

    // Regular straight line for non-self-loops
    const dx = childPosition.x - nodePosition.x;
    const dy = childPosition.y - nodePosition.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
      return null;
    }

    const unitDx = dx / length;
    const unitDy = dy / length;
    const offset = 10;

    const newX2 = childPosition.x - unitDx * offset;
    const newY2 = childPosition.y - unitDy * offset;

    return (
      <Fragment key={lineId}>
        {/* Invisible hit area */}
        <line
          x1={nodePosition.x}
          y1={nodePosition.y}
          x2={newX2}
          y2={newY2}
          stroke="transparent"
          strokeWidth="25"
          onMouseEnter={(event) => highlightLine(event, lineId, edgeTooltipData)}
          onMouseLeave={unhighlightLine}
        />
        {/* Actual line */}
        <line
          x1={nodePosition.x}
          y1={nodePosition.y}
          x2={newX2}
          y2={newY2}
          stroke={highlightedLine === lineId ? "blue" : "lightgrey"}
          strokeWidth={highlightedLine === lineId ? "3" : "2"}
          markerEnd={
            highlightedLine === lineId ? "url(#arrow-blue)" : "url(#arrow)"
          }
        />
      </Fragment>
    );
  };

  const renderNodes = (nodes) => {
    const lines = [];
    const circles = [];
    const renderedLines = new Set();
    const localRenderedNodes = new Set();

    function handleChildLine(node, child, childIndex, params, renderedLines, renderLine, lines) {
      const lineKey = `${node[params.primaryKey]}-${child[params.primaryKey]}`;
      if (renderedLines.has(lineKey)) return;

      renderedLines.add(lineKey);
      const line = renderLine(node, child, childIndex);
      if (line) lines.push(line);
    }

    function processChildren(children, node, params, renderedLines, renderLine, lines) {
      for (let childIndex = 0; childIndex < children.length; childIndex++) {
        const child = children[childIndex];
        handleChildLine(node, child, childIndex, params, renderedLines, renderLine, lines);
      }
    }

    const collectRenderData = (nodes) => {
      nodes.forEach((node) => {
        const uniqueKey = `${node[params.primaryKey]}-${node.level}`;
        if (localRenderedNodes.has(uniqueKey)) return; // Prevent infinite recursion

        localRenderedNodes.add(uniqueKey);

        processChildren(node.children, node, params, renderedLines, renderLine, lines);

        // Highlight logic for origin node
        const isOrigin =
          params.originId &&
          node[params.primaryKey]?.toString() === params.originId?.toString();

        // Node rendering with highlight if origin
        circles.push(
          <Fragment key={uniqueKey}>
            {isOrigin && (
              <circle
                cx={nodePositions[node[params.primaryKey]].x}
                cy={nodePositions[node[params.primaryKey]].y}
                r="18"
                fill="none"
                stroke="#2196f3"
                strokeWidth="5"
              />
            )}
            <circle
              cx={nodePositions[node[params.primaryKey]].x}
              cy={nodePositions[node[params.primaryKey]].y}
              r={highlightedNode === node[params.primaryKey] ? 14 : 0}
              fill={node.dotBorderColor ? node.dotBorderColor : node.dotColor}
            ></circle>
            <circle
              cx={nodePositions[node[params.primaryKey]].x}
              cy={nodePositions[node[params.primaryKey]].y}
              r="12"
              fill={node.dotBorderColor ? node.dotBorderColor : node.dotColor}
            ></circle>
            <circle
              cx={nodePositions[node[params.primaryKey]].x}
              cy={nodePositions[node[params.primaryKey]].y}
              r="8"
              fill={node.dotColor}
              onClick={(event) => handleNodeClick(event, node)}
              onMouseEnter={(event) => showTooltip(node, event)}
              onMouseLeave={hideTooltip}
              style={{ cursor: "pointer" }}
            ></circle>
            <text
              x={nodePositions[node[params.primaryKey]].x}
              y={
                isOrigin
                  ? nodePositions[node[params.primaryKey]].y - 30
                  : nodePositions[node[params.primaryKey]].y - 20
              }
              textAnchor="middle"
              style={{
                fontSize: "10px",
                fontWeight: "bold",
                fill: "#111",
                textDecoration: isOrigin ? "underline" : "none",
                textShadow: isOrigin ? "0 0 6px #fff, 0 0 2px #2196f3" : "none", // bluish shadow
              }}
              transform={`rotate(30, ${nodePositions[node[params.primaryKey]].x
                }, ${nodePositions[node[params.primaryKey]].y - 20})`}
            >
              {node.label}
            </text>
          </Fragment>
        );

        // Only recurse into children if not already rendered
        node.children.forEach((child) => {
          const childKey = `${child[params.primaryKey]}-${child.level}`;
          if (!localRenderedNodes.has(childKey)) {
            collectRenderData([child]);
          }
        });
      });
    };

    collectRenderData(nodes);

    // After rendering regular lines, add self-loops (curved lines)
    if (normalizedData.selfLoops && normalizedData.selfLoops.length > 0) {
      normalizedData.selfLoops.forEach((loop, index) => {
        const node = normalizedData.levels
          .flatMap((level) => level.points)
          .find((n) => n[params.primaryKey] === loop.entityId);

        if (node && nodePositions[node[params.primaryKey]]) {
          const childNode = {
            ...node,
            generation: loop.toGeneration,
          };

          const lineKey = `self-loop-${node[params.primaryKey]}-${loop.fromGeneration
            }-${loop.toGeneration}`;

          if (!renderedLines.has(lineKey)) {
            renderedLines.add(lineKey);
            const selfLoopLine = renderLine(
              node,
              childNode,
              `self-${index}`,
              true
            );
            if (selfLoopLine) {
              lines.push(selfLoopLine);
            }
          }
        }
      });
    }

    return (
      <Fragment>
        {lines}
        {circles}
      </Fragment>
    );
  };

  // Render the menu
  const renderMenu = () => {
    if (!menuVisible) return null;

    console.log("Rendering menu for node:", selectedNode);
    console.log("Menu Params:", params);
    return (
      <div
        className="DAGMenu"
        style={{
          top: menuPosition.y,
          left: menuPosition.x,
          maxWidth: levelSpacing,
        }}
      >
        {(params.menu && params.menu?.defaultButtons) ||
          (!params.menu &&
            defaultButtons.map((button, index) => (
              <button
                key={"default_button_" + index}
                onClick={() => button.action(selectedNode)}
                disabled={button.label == "Data Lineage" && !selectedNode.type_id}
              >
                {button.label}
              </button>
            )))}

        {params.menu?.buttons &&
          params.menu?.buttons.map((button, index) => (
            <button
              key={"menu_button_" + index}
              onClick={() => button.action(selectedNode)}
            >
              {button.label}
            </button>
          ))}
      </div>
    );
  };

  // Render the tooltip
  const renderTooltip = () => {
    if (!tooltipVisible) return null;

    return (
      params.nodeTooltipKeys && (
        <div
          className="LineageTooltip"
          style={{
            position: "absolute",
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            backgroundColor: "white",
            border: "1px solid black",
            padding: "5px",
            zIndex: 1000,
            pointerEvents: "none", // Ensure the tooltip does not interfere with mouse events
          }}
        >
          {params.nodeTooltipKeys.map((tooltipKey, index) => (
            <div key={"tooltip_" + index}>
              <strong>{tooltipKey.displayKey}: </strong>{" "}
              {tooltipNode[tooltipKey.key] || "N/A"}
            </div>
          ))}
        </div>
      )
    );
  };

  const renderLineTooltip = () => {
    if (!lineTooltipVisible) return null;

    return (
      params.lineTooltipKeys && (
        <div
          className="LineageTooltip"
          style={{
            position: "absolute",
            top: lineTooltipPosition.y,
            left: lineTooltipPosition.x,
            backgroundColor: "white",
            border: "1px solid black",
            padding: "5px",
            zIndex: 1000,
            pointerEvents: "none", // Ensure the tooltip does not interfere with mouse events
          }}
        >
          {params.lineTooltipKeys.map((tooltipKey, index) => {
            let value = lineTooltip[tooltipKey.key];
            let displayValue = "N/A";
            if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }
            else if (tooltipKey.key == "process_revision_release_date" && value) {
              displayValue = new Date(value).toISOString().split("T")[0];
            }
            else if (value !== undefined && value !== null && value !== "") {
              displayValue = value;
            }
            return (
              <div key={"tooltip_" + index}>
                <strong>{tooltipKey.displayKey}: </strong>
                {displayValue}
              </div>
            );
          })}
        </div>
      )
    );
  };

  const renderLegendModal = () => {
    if (!legendVisible) return null;

    const isVersionLineage = params.primaryKey === "version_id";

    // Collect unique rings and nodes for legend
    const ringMap = new Map();
    const nodeMap = new Map();

    normalizedData.levels.forEach((level) => {
      level.points.forEach((node) => {
        const className = node.class_name || "";
        const statusMain = node.status_name
          ? node.status_name.split(" - ")[0]
          : "";
        if (isVersionLineage) {
          if (statusMain && !ringMap.has(statusMain)) {
            ringMap.set(statusMain, node);
          }
          if (className && !nodeMap.has(className)) {
            nodeMap.set(className, node);
          }
        } else {
          if (className && !ringMap.has(className)) {
            ringMap.set(className, node);
          }
        }
      });
    });

    return (
      <>
        {/* Overlay stays fixed and does not move */}
        <div className="lineageLegendOverlay" />
        {/* Movable legend modal */}
        <div
          className="lineageLegendModal"
          style={{
            position: "fixed",
            left: legendPosition.x,
            top: legendPosition.y,
            zIndex: 2001,
            pointerEvents: "auto",
          }}
        >
          <div
            className="legendSectionTitle"
            onMouseDown={handleLegendMouseDown}
          >
            <strong>Point of view: </strong>
          </div>
          <button
            onClick={() => setLegendVisible(false)}
            className="closeLegendButton"
            aria-label="Close legend"
          >
            &times;
          </button>
          <ul className="legendList">
            <li className="legendRow" key="origin-ring">
              <div className="legendSection legendOriginRing">
                <svg width="36" height="36" className="legendOriginRingSvg">
                  <circle
                    cx="18"
                    cy="18"
                    r="14"
                    fill="none"
                    stroke="#2196f3"
                    strokeWidth="5"
                  />
                </svg>
                <span className="legendLabel">You are here</span>
              </div>
            </li>
          </ul>
          {isVersionLineage ? (
            <div className="legendSectionsRow">
              <div className="legendSectionCol">
                <div className="legendSectionTitle">
                  <strong>Status: </strong>
                </div>
                <ul className="legendList">
                  {[...ringMap.entries()].map(([statusMain, node], idx) => (
                    <li className="legendRow" key={idx}>
                      <div className="legendSection legendRing">
                        <svg width="36" height="36" className="legendRingSvg">
                          <circle
                            cx="18"
                            cy="18"
                            r="14"
                            fill="#fff"
                            stroke={
                              node.dotBorderColor
                                ? node.dotBorderColor
                                : node.dotColor
                            }
                            strokeWidth="6"
                          />
                        </svg>
                        <span className="legendLabel">{statusMain}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="legendSectionCol">
                <div className="legendSectionTitle">
                  <strong>Class: </strong>
                </div>
                <ul className="legendList">
                  {[...nodeMap.entries()].map(([className, node], idx) => (
                    <li className="legendRow" key={idx}>
                      <div className="legendSection legendNode">
                        <svg width="36" height="36" className="legendNodeSvg">
                          <circle cx="18" cy="18" r="8" fill={node.dotColor} />
                        </svg>
                        <span className="legendLabel">{className}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <>
              <div className="legendSectionTitle">
                <strong>Class: </strong>
              </div>
              <ul className="legendList">
                {[...ringMap.entries()].map(([className, node], idx) => (
                  <li className="legendRow" key={idx}>
                    <div className="legendPoint">
                      <svg width="36" height="36" className="legendPointSvg">
                        <circle
                          cx="18"
                          cy="18"
                          r="14"
                          fill="#fff"
                          stroke={
                            node.dotBorderColor
                              ? node.dotBorderColor
                              : node.dotColor
                          }
                          strokeWidth="6"
                        />
                        <circle cx="18" cy="18" r="13" fill={node.dotColor} />
                      </svg>
                      <span className="legendLabel">{className}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <div
      onClick={closeMenu}
      style={{ pointerEvents: legendVisible ? "none" : "auto" }}
    >
      <div className="zoom-buttons">
        <Button
          color="primary"
          outline
          className="mb-2 shadow-sm"
          onClick={handleZoomIn}
        >
          <i className="bi bi-zoom-in"></i> Zoom In
        </Button>

        <Button
          color="primary"
          outline
          className="mb-2 shadow-sm"
          onClick={handleZoomOut}
        >
          <i className="bi bi-zoom-out"></i> Zoom Out
        </Button>

        <Button
          color="primary"
          outline
          className="mb-2 shadow-sm"
          onClick={handleResetZoom}
        >
          <i className="bi bi-arrows-fullscreen"></i> Reset
        </Button>

        <Button
          color="primary"
          outline
          className="mb-2 shadow-sm"
          onClick={() => setLegendVisible(true)}
        >
          <i className="bi bi-info-circle"></i> Legend
        </Button>

        <div className="zoom-options-divider"></div>

        <Button
          color="success"
          outline
          className="mb-2 shadow-sm"
          onClick={handleDownloadPNG}
        >
          <i className="bi bi-download"></i> PNG
        </Button>

        <div className="zoom-options-divider"></div>


        <FormGroup check className="p-2 mb-2   rounded shadow-sm">
          <Input
            type="checkbox"
            id="optimizeLayoutCheck"
            checked={optimizeLayout}
            onChange={onToggleOptimized}
            className="me-2"
          />
          <Label for="optimizeLayoutCheck" check>
            Optimize layout
          </Label>
        </FormGroup>

        {params.primaryKey === "version_id" && (
          <FormGroup check className="p-2 bg-white rounded shadow-sm">
            <Input
              type="checkbox"
              id="validOnlyCheck"
              checked={validOnly}
              onChange={onToggleValidOnly}
              className="me-2"
            />
            <Label for="validOnlyCheck" check>
              Valid only
            </Label>
          </FormGroup>
        )}
      </div>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "89vh",
          overflow: "hidden",
          cursor: isPanning ? "grabbing" : "grab",
          position: "relative",
        }}
        onMouseDown={handleContainerMouseDown}
      >
        <svg
          ref={svgRef}
          width={Math.max(svgWidth, 1000)}
          height={Math.max(svgHeight, 800)}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
            transformOrigin: `${transformOrigin.x}px ${transformOrigin.y}px`,
            userSelect: "none",
            background: "#fff"
          }}
        >
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="10"
              refY="5"
              orient="auto"
              markerWidth="6"
              markerHeight="6"
            >
              <polygon points="0 0, 10 5, 0 10" fill="lightgrey" />
            </marker>
            <marker
              id="arrow-blue"
              viewBox="0 0 10 10"
              refX="10"
              refY="5"
              orient="auto"
              markerWidth="6"
              markerHeight="6"
            >
              <polygon points="0 0, 10 5, 0 10" fill="blue" />
            </marker>
          </defs>
          {renderNodes(treeData)}
        </svg>
      </div>
      {renderMenu()}
      {renderTooltip()}
      {renderLineTooltip()}
      {renderLegendModal()}
    </div>
  );
};

LineageDrawer.propTypes = {
  params: PropTypes.shape({
    menu: PropTypes.shape({
      defaultButtons: PropTypes.bool.isRequired,
      buttons: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          action: PropTypes.func.isRequired,
        })
      ).isRequired,
    }),
    nodeTooltipKeys: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        displayKey: PropTypes.string.isRequired,
      })
    ),
    lineTooltipKeys: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        displayKey: PropTypes.string.isRequired,
      })
    ),
    primaryKey: PropTypes.string.isRequired,
  }),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      idx: PropTypes.number.isRequired,
      origin_version_id: PropTypes.number,
      origin_version_name: PropTypes.string,
      origin_version_description: PropTypes.string,
      origin_type_id: PropTypes.string,
      origin_type_name: PropTypes.string,
      origin_type_description: PropTypes.string,
      origin_class_name: PropTypes.string,
      origin_domain_id: PropTypes.string,
      origin_domain_name: PropTypes.string,
      origin_generation: PropTypes.number.isRequired,
      version_id: PropTypes.number,
      version_name: PropTypes.string,
      version_description: PropTypes.string,
      type_id: PropTypes.string,
      type_name: PropTypes.string,
      type_description: PropTypes.string,
      class_name: PropTypes.string,
      domain_id: PropTypes.string,
      domain_name: PropTypes.string,
      generation: PropTypes.number.isRequired,
      process_id: PropTypes.string,
      process_name: PropTypes.string,
      process_description: PropTypes.string,
      process_type_name: PropTypes.string,
      process_is_manual_validation: PropTypes.bool,
      dotColor: PropTypes.string,
      dotColorOrigin: PropTypes.string,
      dotBorderColor: PropTypes.string,
      dotBorderColorOrigin: PropTypes.string,
      reference_date: PropTypes.string,
      origin_reference_date: PropTypes.string,
      status_name: PropTypes.string,
      origin_status_name: PropTypes.string,
      status_id: PropTypes.number,
      origin_status_id: PropTypes.number,
    })
  ).isRequired,
};

export default LineageDrawer;
